/**
 * JoinPilotModal — the pilot lead-capture funnel. A "Join the pilot" CTA opens
 * this dialog (URL-driven: any link to `?join=pilot` opens it, `&tier=` pre-sets
 * the tier). It captures the visitor's details and emails them to the team via
 * the Resend worker (website/email-worker) — the same no-GCP endpoint as the
 * contact form. The team then pre-vets and sends a Stripe payment link by hand,
 * so there's no orphan-subscription problem and nothing depends on the API/DB.
 *
 * Mounted once by MarketingLayout, so it's available on every page.
 */
import { useState, useEffect, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './dialog';
import { Input } from './input';
import { Label } from './label';
import { Button } from './button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import { Textarea } from './textarea';
import { PRICING_TIER_SUMMARIES } from './blocks/PricingTierDetail';
import { trackEvent } from '../analytics/track';

export interface JoinPilotModalProps {
  /** Resend email-worker endpoint (lead capture). From the site manifest. */
  readonly endpoint?: string;
  /** Funnel copy — supplied from CONVERSION.pilot in the site manifest. */
  readonly title?: string;
  readonly description?: string;
  readonly submitLabel?: string;
  readonly successTitle?: string;
  readonly successBody?: string;
}

type Status = 'idle' | 'submitting' | 'success' | 'error';

const TIER_OPTIONS = [
  ...PRICING_TIER_SUMMARIES.map((t) => ({ value: t.id, label: t.name })),
  { value: 'undecided', label: 'Not sure yet' },
];

export function JoinPilotModal({
  endpoint = 'https://inferrex-contact.workers.dev',
  title = 'Join the pilot',
  description = "Tell us about your stack. We'll review, then send you next steps and a secure payment link — no card needed yet.",
  submitLabel = 'Request pilot access',
  successTitle = "You're in the queue.",
  successBody = "Thanks — we'll review your details and email you next steps, including a secure payment link, shortly.",
}: JoinPilotModalProps) {
  const [params, setParams] = useSearchParams();
  const open = params.get('join') === 'pilot';
  const urlTier = (params.get('tier') ?? '').toLowerCase();

  const [tier, setTier] = useState(urlTier);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState('');

  // The modal is mounted globally, so sync the tier from the URL each time it
  // opens (a client-side "Subscribe" click sets ?tier= after mount).
  useEffect(() => { if (open) setTier(urlTier); }, [open, urlTier]);

  const close = () => {
    const next = new URLSearchParams(params);
    next.delete('join');
    next.delete('tier');
    setParams(next, { replace: true });
    // reset for next open
    setTimeout(() => { setStatus('idle'); setError(''); }, 200);
  };

  const tierName = TIER_OPTIONS.find((t) => t.value === tier)?.label;

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (status === 'submitting') return;
    const data = new FormData(e.currentTarget);
    // `interest` MUST be one of the server enum (pilot|cloud|enterprise|sovereign|exploring) —
    // /v1/contact validates it strictly, so the old free-text `Pilot — <tier>` 400'd and EVERY
    // pilot submission was silently lost. Send 'pilot' and carry the tier in `message` instead.
    const tierNote = tierName ? `Pilot tier: ${tierName}.` : 'Pilot — tier undecided.';
    const typedMessage = String(data.get('message') ?? '').trim();
    const body: Record<string, string> = {
      name: String(data.get('name') ?? '').trim(),
      email: String(data.get('email') ?? '').trim(),
      company: String(data.get('company') ?? '').trim(),
      interest: 'pilot',
      message: typedMessage ? `${tierNote} ${typedMessage}` : tierNote,
    };
    const systems = String(data.get('systems') ?? '').trim();
    if (systems) body.systems = systems;

    setStatus('submitting');
    setError('');
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok && res.status !== 429) throw new Error('Something went wrong.');
      setStatus('success');
      trackEvent('generate_lead', { form: 'pilot', ...(tier ? { tier } : {}) });
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    }
  };

  const field = 'space-y-1.5';
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent size="md">
        {status === 'success' ? (
          <div className="py-4 text-center">
            <DialogTitle className="font-display text-2xl">{successTitle}</DialogTitle>
            <p className="mt-2 text-muted-foreground">
              {successBody}{' '}
              If it's urgent, reach us at{' '}
              <a href="mailto:hello@inferrex.com" className="text-primary underline-offset-2 hover:underline">hello@inferrex.com</a>.
            </p>
            <Button className="mt-5" onClick={close}>Done</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{title}</DialogTitle>
              <DialogDescription>{description}</DialogDescription>
            </DialogHeader>
            <form onSubmit={onSubmit} className="space-y-4" noValidate>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className={field}>
                  <Label htmlFor="jp-name">Name</Label>
                  <Input id="jp-name" name="name" required autoComplete="name" placeholder="Your name" />
                </div>
                <div className={field}>
                  <Label htmlFor="jp-email">Work email</Label>
                  <Input id="jp-email" name="email" type="email" required autoComplete="email" placeholder="you@company.com" />
                </div>
                <div className={field}>
                  <Label htmlFor="jp-company">Company</Label>
                  <Input id="jp-company" name="company" required autoComplete="organization" placeholder="Company name" />
                </div>
                <div className={field}>
                  <Label htmlFor="jp-tier">Tier of interest</Label>
                  <Select value={tier} onValueChange={setTier}>
                    <SelectTrigger id="jp-tier"><SelectValue placeholder="Choose one" /></SelectTrigger>
                    <SelectContent>
                      {TIER_OPTIONS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className={field}>
                <Label htmlFor="jp-systems">What are you trying to connect? <span className="text-muted-foreground">(optional)</span></Label>
                <Input id="jp-systems" name="systems" placeholder="e.g. Salesforce, a billing system, an internal API" maxLength={1000} />
              </div>
              <div className={field}>
                <Label htmlFor="jp-message">Anything else? <span className="text-muted-foreground">(optional)</span></Label>
                <Textarea id="jp-message" name="message" rows={3} placeholder="Tell us a little about what you're solving." maxLength={5000} />
              </div>
              {status === 'error' && (
                <p className="text-sm text-destructive">
                  {error} You can always email <a href="mailto:hello@inferrex.com" className="underline underline-offset-2">hello@inferrex.com</a>.
                </p>
              )}
              <Button type="submit" size="lg" className="w-full" disabled={status === 'submitting'}>
                {status === 'submitting' ? 'Sending…' : submitLabel}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
